import { DatabaseIcon } from '@/components/icons/database-icon'
import { EmergenceIcon } from '@/components/icons/emergence-icon'
import { FileIcon } from '@/components/icons/file-icon'
import { FolderIcon } from '@/components/icons/folder-icon'
import { PlusIcon } from '@/components/icons/plus-icon'
import { TrashIcon } from '@/components/icons/trash-icon'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { DataTableColumnHeader } from '@/components/ui/data-table'
import { Input } from '@/components/ui/input'
import { Progress } from '@/components/ui/progress'
import {
  Table,
  TableBody,
  TableCell,
  TableErrorRow,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeletonRows,
} from '@/components/ui/table'
import { toast } from '@/components/ui/toaster'
import {
  DataAssetType,
  useDataAssetsQuery,
  type DataAsset,
} from '@/hooks/api/use-data-assets-query'
import {
  useDataQualScoreCardsQuery,
  useMetaDataScoreCardsQuery,
} from '@/hooks/api/use-datagovernance-dashboard-query'
import { useMultiSelect } from '@/hooks/use-multi-select-table-row'
import { cn } from '@/lib/utils'
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table'
import { T, useTranslate } from '@tolgee/react'
import { SearchIcon, Target, TrendingUp } from 'lucide-react'
import React from 'react'
import { useAuth } from '../_auth/auth-hooks'
import { PageTemplate } from '../_common/page-template'
import {
  AddDataAssetDialog,
  DeleteDataAssetsDialog,
  DescriptionCell,
  NameButton,
  useTableSelection,
} from './_common'
import { SERVICE_CONFIG } from './_common/constants'
import { useDataCatalogNavigation } from './_common/hooks/use-data-catalog-breadcrumbs'
import { useDataAssetDelete } from './_common/hooks/use-delete-data-asset'
import { ScoreCardsUpdate } from './dashboard/_components/_common/scorecards-update'
import { getStyles } from './dashboard/utils'

type DataCatalogTableProps = Readonly<{
  showAddButton?: boolean
}>

function DataCatalogTable({ showAddButton = true }: DataCatalogTableProps) {
  const { t } = useTranslate()
  const { user } = useAuth()
  const { openSchemaTab, openMetadataTab, openDataGovernanceDashboardTab } =
    useDataCatalogNavigation()

  const [isAddDataDialogOpen, setIsAddDataDialogOpen] = React.useState(false)
  const [sorting, setSorting] = React.useState<SortingState>([])
  const [globalFilter, setGlobalFilter] = React.useState('')

  const dataAssetsQuery = useDataAssetsQuery()

  // Metadata score query for overall score
  const metadataScoreQuery = useMetaDataScoreCardsQuery({
    filters: {
      database: 'all',
      schema: 'all',
      table: 'all',
      sensitivity: 'all',
    },
    queryOptions: {
      enabled: true,
    },
  })

  const dataQualityScore = useDataQualScoreCardsQuery({
    filters: {
      database: 'all',
      schema: 'all',
      table: 'all',
      sensitivity: 'all',
    },
    queryOptions: {
      enabled: true,
    },
  })

  const isDeletable = React.useCallback(
    (asset: DataAsset) => asset.dataType !== DataAssetType.DATABASE,
    [],
  )

  const dataAssets = React.useMemo(
    () => dataAssetsQuery.data?.data ?? [],
    [dataAssetsQuery.data?.data],
  )

  const multiSelect = useMultiSelect(dataAssets, isDeletable)

  // Calculate selectable assets and header checkbox state
  const selectableAssets = React.useMemo(
    () => dataAssets.filter(isDeletable),
    [dataAssets, isDeletable],
  )

  const { areAllItemsSelected, handleHeaderCheckboxChange, handleItemSelection } =
    useTableSelection({
      items: selectableAssets,
      selectedIds: multiSelect.selectedIds,
      getId: (asset) => asset.id,
      onSelectionChange: (ids) => {
        multiSelect.clearSelection()
        ids.forEach((id) => multiSelect.toggleItem(id))
      },
    })

  const deleteHandlers = useDataAssetDelete({
    dataAssets,
    selectedIds: multiSelect.selectedIds,
    selectedCount: multiSelect.selectedCount,
    clearSelection: multiSelect.clearSelection,
  })

  const handleAddData = React.useCallback(() => {
    setIsAddDataDialogOpen(true)
  }, [])

  const handleAddDataDialogClose = React.useCallback((open: boolean) => {
    setIsAddDataDialogOpen(open)
  }, [])

  const handleNameButtonClick = React.useCallback(
    ({ dataType, name, tableNames, schemaName }: DataAsset) => {
      if (dataType === 'DATABASE') {
        openSchemaTab(name, 'database')
      } else if (dataType === 'DATA') {
        const tableName = tableNames?.[0]

        if (!schemaName || !tableName) {
          toast.error('Missing schema or table name')
          return
        }

        openMetadataTab(SERVICE_CONFIG.DATABASE_NAME, schemaName, tableName)
      } else {
        toast.error('Failed to open data asset')
        console.error('Tab not created for data type', dataType)
      }
    },
    [openSchemaTab, openMetadataTab],
  )

  // Column definitions
  const columnDefs = React.useMemo<ColumnDef<DataAsset>[]>(() => {
    const selectColumn: ColumnDef<DataAsset> = {
      id: 'select',
      size: 50,
      header: () => (
        <Checkbox
          variant='neutral'
          checked={areAllItemsSelected}
          showUnselectIcon={areAllItemsSelected}
          onCheckedChange={handleHeaderCheckboxChange}
          disabled={selectableAssets.length === 0}
          title={(() => {
            if (selectableAssets.length === 0) {
              return 'No assets to select'
            }

            if (areAllItemsSelected) {
              return 'Unselect all'
            }

            return 'Select all'
          })()}
        />
      ),
      cell: ({ row }) => {
        const asset = row.original
        const isSelected = multiSelect.selectedIds.includes(asset.id)
        const canSelect = isDeletable(asset)

        return (
          <Checkbox
            variant='neutral'
            checked={isSelected}
            onCheckedChange={() => handleItemSelection(asset.id)}
            disabled={!canSelect}
            title={(() => {
              if (!canSelect) {
                return 'Emergence AI assets cannot be modified'
              }

              if (isSelected) {
                return 'Unselect'
              }

              return 'Select'
            })()}
          />
        )
      },
    }

    const baseColumns: ColumnDef<DataAsset>[] = [
      {
        size: 200,
        accessorKey: 'name' as keyof DataAsset,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} titleKey='dataCatalog.table.headers.name'>
            <T keyName='dataCatalog.table.headers.name' />
          </DataTableColumnHeader>
        ),
        cell: ({ row: { original: dataAsset } }) => {
          const disable = dataAsset.source === 'CHAT'
          return (
            <NameButton
              name={dataAsset.name}
              onClick={() => handleNameButtonClick(dataAsset)}
              disabled={disable}
              title={
                disable
                  ? 'Metadata and data quality workflows aren’t supported for chat uploads.'
                  : `view ${dataAsset.dataType === 'DATABASE' ? 'schema' : 'metadata'} for ${dataAsset.name}`
              }
              enableTooltipForDisabled
            />
          )
        },
      },
      {
        accessorKey: 'shortDescription' as keyof DataAsset,
        header: () => <T keyName='dataCatalog.table.headers.description' />,
        cell: ({ cell }) => {
          const rowData = cell.row.original
          const description =
            rowData.dataType === 'DATABASE' ? rowData.description : rowData.shortDescription

          return <DescriptionCell data={description} />
        },
        enableSorting: false,
        size: 400,
      },
      {
        accessorKey: 'dataType' as keyof DataAsset,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} titleKey='dataCatalog.table.headers.type'>
            <T keyName='dataCatalog.table.headers.type' />
          </DataTableColumnHeader>
        ),
        cell: ({ cell }) => {
          const type = cell.getValue<DataAssetType>()
          return (
            <div className='flex items-center gap-1 py-2'>
              {getDataTypeIcon(type)}
              <p className='font-medium'>{getDataTypeBadgeText(type)}</p>
            </div>
          )
        },
      },
      {
        accessorKey: 'owner' as keyof DataAsset,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} titleKey='dataCatalog.table.headers.owner'>
            <T keyName='dataCatalog.table.headers.owner' />
          </DataTableColumnHeader>
        ),
        cell: ({ row }) => {
          const { dataType } = row.original
          return (
            <p className='flex items-center gap-2 text-center'>
              {dataType === DataAssetType.DATABASE ? (
                <>
                  <EmergenceIcon className='size-6' />
                  <T keyName='dataCatalog.table.owner.emergence' />
                </>
              ) : (
                <>
                  <span className='flex size-6 shrink-0 items-center justify-center rounded-full bg-mango-300 text-xs font-normal text-neutral-white'>
                    {user?.firstName?.[0]}
                  </span>
                  {user?.fullName}
                </>
              )}
            </p>
          )
        },
      },
    ]

    return [selectColumn, ...baseColumns]
  }, [
    areAllItemsSelected,
    handleHeaderCheckboxChange,
    handleItemSelection,
    selectableAssets.length,
    multiSelect.selectedIds,
    multiSelect.toggleItem,
    isDeletable,
    user?.firstName,
    user?.fullName,
    handleNameButtonClick,
  ])

  const table = useReactTable<DataAsset>({
    data: dataAssets,
    columns: columnDefs,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    sortDescFirst: true,
    globalFilterFn: (row, columnId, value) => {
      // filter by the name column
      const name = row.getValue('name') as string
      return name.toLowerCase().includes(value.toLowerCase())
    },
    state: { sorting, globalFilter },
    enableColumnFilters: false,
  })

  const { rows } = table.getRowModel()
  const isInitialLoading = dataAssetsQuery.isPending
  const isTableLoading = isInitialLoading && rows.length === 0

  return (
    <div className='flex w-full flex-col gap-6'>
      <PageTemplate.Header className='flex flex-wrap items-center justify-between gap-4'>
        <div className='min-w-0'>
          <PageTemplate.H4>
            <T keyName='dataCatalog.subtitle' />
          </PageTemplate.H4>
        </div>
        <div className='flex flex-shrink-0 flex-wrap items-center gap-4'>
          <div className='relative'>
            <SearchIcon className='absolute left-3 top-1/2 size-4 -translate-y-1/2 text-neutral-200' />
            <Input
              placeholder='Search'
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className='w-80 pl-10'
            />
          </div>
          <AddButton onClick={handleAddData} />
        </div>
      </PageTemplate.Header>

      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-6'>
          <Button
            unstyled
            className='flex items-center gap-2 rounded-lg p-2 hover:bg-neutral-50 disabled:cursor-not-allowed'
            disabled={metadataScoreQuery.isLoading}
            onClick={() => {
              openDataGovernanceDashboardTab('metaData', 'all', 'all', 'all', 'database', 'all')
            }}
          >
            <div className='flex flex-col items-start gap-1'>
              <div className='flex items-center gap-2'>
                <p className='text-sm font-normal'>Metadata Score</p>
                <TrendingUp className='size-4 text-neutral-200' />
              </div>
              {metadataScoreQuery.isLoading ? (
                <div className='mt-2 h-2 w-24 animate-pulse rounded-full bg-gray-200' />
              ) : (
                <div className='flex items-center gap-2'>
                  <span
                    className={`text-sm font-medium ${getStyles(metadataScoreQuery.data?.score || 0).text}`}
                  >
                    {metadataScoreQuery.data?.score
                      ? `${Math.round(metadataScoreQuery.data.score)}%`
                      : 'N/A'}
                  </span>
                  <Progress
                    value={metadataScoreQuery.data?.score || 0}
                    className='h-1 w-16'
                    color={getStyles(metadataScoreQuery.data?.score || 0).progress}
                  />
                </div>
              )}
            </div>
          </Button>
          <Button
            unstyled
            className='flex items-center gap-2 rounded-lg p-2 hover:bg-neutral-50 disabled:cursor-not-allowed'
            disabled={dataQualityScore.isLoading}
            onClick={() => {
              openDataGovernanceDashboardTab('dataQuality', 'all', 'all', 'all', 'database', 'all')
            }}
          >
            <div className='flex flex-col items-start gap-1'>
              <div className='flex items-center gap-2'>
                <p className='text-sm font-normal'>Data Quality Score</p>
                <Target className='size-4 text-neutral-200' />
              </div>
              {dataQualityScore.isLoading ? (
                <div className='mt-2 h-2 w-24 animate-pulse rounded-full bg-gray-200' />
              ) : (
                <div className='flex items-center gap-2'>
                  <span
                    className={`text-sm font-medium ${getStyles(dataQualityScore.data?.overall_assessment.percentage_score || 0).text}`}
                  >
                    {dataQualityScore.data?.overall_assessment.percentage_score
                      ? `${Math.round(dataQualityScore.data?.overall_assessment.percentage_score)}%`
                      : 'N/A'}
                  </span>
                  <Progress
                    value={dataQualityScore.data?.overall_assessment.percentage_score || 0}
                    className='h-1 w-16'
                    color={
                      getStyles(dataQualityScore.data?.overall_assessment.percentage_score || 0)
                        .progress
                    }
                  />
                </div>
              )}
            </div>
          </Button>
        </div>
        <div className='flex items-center gap-2'>
          <span className='text-sm'>{multiSelect.selectedCount} items selected</span>
          <Button
            unstyled
            disabled={multiSelect.selectedCount === 0}
            onClick={deleteHandlers.handleDeleteSelected}
            title='Delete'
            className={cn(
              'transition-colors',
              'hover:text-error-300 disabled:hover:text-grey-300',
              'disabled:text-grey-300',
            )}
          >
            <TrashIcon className='size-8' />
          </Button>
        </div>
      </div>
      {metadataScoreQuery.isFetching || dataQualityScore.isFetching ? <ScoreCardsUpdate /> : <></>}

      <Table className='w-full'>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  style={{ width: `${header.getSize()}px` }}
                  data-state={
                    (header.column.getIsSorted() || header.column.getIsFiltered()) && 'selected'
                  }
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {isTableLoading && <TableSkeletonRows rowCount={3} cellCount={columnDefs.length} />}

          {dataAssetsQuery.isError && <TableErrorRow colSpan={columnDefs.length} />}

          {dataAssetsQuery.isSuccess &&
            rows.length > 0 &&
            rows.map((row) => (
              <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    style={{ width: `${cell.column.getSize()}px` }}
                    data-state={
                      (cell.column.getIsSorted() || cell.column.getIsFiltered()) && 'selected'
                    }
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}

          {dataAssetsQuery.isSuccess && rows.length === 0 && !dataAssetsQuery.isFetching && (
            <TableRow>
              <TableCell colSpan={columnDefs.length} className='h-24 text-center'>
                <T keyName='dataCatalog.table.messages.noDataObjects' />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {showAddButton && (
        <AddDataAssetDialog open={isAddDataDialogOpen} onOpenChange={handleAddDataDialogClose} />
      )}

      <DeleteDataAssetsDialog
        open={deleteHandlers.isDeleteDialogOpen}
        onOpenChange={deleteHandlers.setIsDeleteDialogOpen}
        selectedCount={multiSelect.selectedCount}
        onConfirm={deleteHandlers.handleConfirmDelete}
        isLoading={deleteHandlers.isLoading}
      />
    </div>
  )
}

// 👇 Utility functions

const getDataTypeIcon = (dataType: DataAssetType): JSX.Element => {
  switch (dataType) {
    case 'DATABASE':
      return <DatabaseIcon className='size-7 text-neutral-200' />
    case 'FOLDER':
      return <FolderIcon className='size-7 text-neutral-200' />
    case 'DOCUMENT':
      return <FileIcon className='size-7 text-neutral-200' />
    case 'DATA':
      return <FileIcon className='size-7 text-neutral-200' />
    default:
      return <DatabaseIcon className='size-7 text-neutral-200' />
  }
}

const getDataTypeBadgeText = (dataType: DataAssetType): string => {
  switch (dataType) {
    case 'DATABASE':
      return 'Database'
    case 'FOLDER':
      return 'Folder'
    case 'DOCUMENT':
      return 'Document'
    case 'DATA':
      return 'Data'
    default:
      return dataType
  }
}

interface AddButtonProps {
  onClick: () => void
  isFromTable?: boolean
}

function AddButton({ onClick }: Readonly<AddButtonProps>) {
  const { t } = useTranslate()
  return (
    <Button
      onClick={onClick}
      title={t('dataCatalog.addButton.title.a11y')}
      className='flex shrink-0 items-center gap-2'
    >
      <T keyName='dataCatalog.addButton.title.label' />
      <PlusIcon className='size-3 text-mango-200' />
    </Button>
  )
}

export { DataCatalogTable }
